/**
 * FAAC Story Page
 * Scroll-driven narrative visualization of FAAC data
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { useApp } from '../context/AppContext';
import { useStoryData } from '../hooks/useStoryData';
import { LoadingScreen, ErrorScreen } from '../components/LoadingScreen';
import {
  Scene1Opening,
  Scene2OilGiants,
  Scene3Timeline,
  Scene4NorthSouth,
  Scene5RankingRace,
  Scene6Growth,
  Scene7Volatility,
  Scene8Closing,
} from '../components/story/scenes';
import { ArrowRightIcon } from '../components/icons/StoryIcons';
import { Footer } from '../components/Footer';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

interface StoryPageProps {
  onNavigateToExplorer?: () => void;
}

export function StoryPage({ onNavigateToExplorer }: StoryPageProps) {
  const { isLoading, error, data } = useApp();
  const insights = useStoryData(data);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeScene, setActiveScene] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Cleanup ScrollTrigger on unmount
  useEffect(() => {
    return () => {
      // Kill all ScrollTriggers when leaving the page
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      gsap.killTweensOf('*');
    };
  }, []);
  
  // Scroll to a specific scene
  const scrollToScene = useCallback((index: number) => {
    const scenes = containerRef.current?.querySelectorAll('.story-scene');
    if (scenes?.[index]) {
      // Update active scene immediately for responsive UI
      setActiveScene(index);
      
      // Use GSAP for smooth scrolling
      const scene = scenes[index] as HTMLElement;
      const sceneRect = scene.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const scrollTarget = window.scrollY + sceneRect.top - (viewportHeight / 2) + (sceneRect.height / 2);
      
      gsap.to(window, {
        scrollTo: { y: scrollTarget, autoKill: false },
        duration: 1,
        ease: 'power2.inOut',
      });
    }
  }, []);
  
  // Scene scroll tracking
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Kill any existing ScrollTriggers first
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    
    const scenes = containerRef.current.querySelectorAll('.story-scene');
    
    scenes.forEach((scene, index) => {
      ScrollTrigger.create({
        trigger: scene,
        start: 'top center',
        end: 'bottom center',
        onEnter: () => setActiveScene(index),
        onEnterBack: () => setActiveScene(index),
      });
    });
    
    // Overall progress
    ScrollTrigger.create({
      trigger: containerRef.current,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: (self) => setScrollProgress(self.progress),
    });
    
    // Refresh ScrollTrigger after a short delay to ensure DOM is ready
    const refreshTimeout = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
    
    return () => {
      clearTimeout(refreshTimeout);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [data]);
  
  // Refresh ScrollTrigger when scene changes to ensure animations work
  useEffect(() => {
    const refreshTimeout = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 50);
    return () => clearTimeout(refreshTimeout);
  }, [activeScene]);
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (error) {
    return <ErrorScreen message={error} />;
  }
  
  if (!data || !insights) {
    return <ErrorScreen message="Failed to load story data" />;
  }
  
  const sceneLabels = [
    'The Total',
    'Oil Giants',
    'Timeline',
    'North vs South',
    'Rankings',
    'Growth',
    'Volatility',
    'Explore',
  ];
  
  return (
    <div ref={containerRef} className="relative bg-surface-950 text-white">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-surface-950/80 backdrop-blur-md border-b border-surface-800">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-xl font-display font-bold text-white hover:text-accent-400 transition-colors">
              FAAC<span className="text-accent-500">viz</span>
            </a>
            <span className="hidden sm:inline px-3 py-1 bg-accent-500/20 text-accent-400 text-sm rounded-full">
              Story Mode
            </span>
          </div>
          
          <button
            onClick={onNavigateToExplorer}
            className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg font-medium text-sm hover:bg-accent-400 transition-colors"
          >
            Explorer
            <ArrowRightIcon size={16} />
          </button>
        </div>
        
        {/* Progress bar */}
        <div className="h-0.5 bg-surface-800">
          <div 
            className="h-full bg-gradient-to-r from-accent-400 to-cyan-400 transition-all duration-150"
            style={{ width: `${scrollProgress * 100}%` }}
          />
        </div>
      </header>
      
      {/* Scene Navigation Dots */}
      <nav className="fixed right-6 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col items-end gap-3">
        {sceneLabels.map((label, index) => (
          <button
            key={index}
            onClick={() => scrollToScene(index)}
            className="group relative flex items-center justify-end"
          >
            {/* Label - positioned absolutely to not affect dot alignment */}
            <span className={`
              absolute right-5 whitespace-nowrap text-xs font-medium transition-all duration-300
              ${activeScene === index 
                ? 'opacity-100 translate-x-0 text-accent-400' 
                : 'opacity-0 translate-x-2 text-surface-400 group-hover:opacity-100 group-hover:translate-x-0'}
            `}>
              {label}
            </span>
            {/* Dot - always in the same position */}
            <div className={`
              w-3 h-3 rounded-full border-2 transition-all duration-300 flex-shrink-0
              ${activeScene === index 
                ? 'bg-accent-500 border-accent-500 scale-125' 
                : 'bg-transparent border-surface-600 group-hover:border-surface-400'}
            `} />
          </button>
        ))}
      </nav>
      
      {/* Scroll hint - only shown at the beginning */}
      {activeScene === 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-bounce ">
          <div className="flex flex-col items-center gap-2 text-surface-400 text-sm">
            <span>Scroll to explore</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      )}
      
      {/* Scenes */}
      <main className="pt-20">
        {/* Scene 1: Opening */}
        <div className="story-scene">
          <Scene1Opening 
            insights={insights} 
            features={data.geoFeatures}
            isActive={activeScene === 0}
          />
        </div>
        
        {/* Scene 2: Oil Giants */}
        <div className="story-scene">
          <Scene2OilGiants 
            insights={insights}
            features={data.geoFeatures}
            isActive={activeScene === 1}
          />
        </div>
        
        {/* Scene 3: Timeline */}
        <div className="story-scene">
          <Scene3Timeline 
            insights={insights}
            isActive={activeScene === 2}
          />
        </div>
        
        {/* Scene 4: North vs South */}
        <div className="story-scene">
          <Scene4NorthSouth 
            insights={insights}
            features={data.geoFeatures}
            isActive={activeScene === 3}
          />
        </div>
        
        {/* Scene 5: Ranking Race */}
        <div className="story-scene">
          <Scene5RankingRace 
            insights={insights}
            isActive={activeScene === 4}
          />
        </div>
        
        {/* Scene 6: Growth */}
        <div className="story-scene">
          <Scene6Growth 
            insights={insights}
            isActive={activeScene === 5}
          />
        </div>
        
        {/* Scene 7: Volatility */}
        <div className="story-scene">
          <Scene7Volatility 
            insights={insights}
            isActive={activeScene === 6}
          />
        </div>
        
        {/* Scene 8: Closing */}
        <div className="story-scene">
          <Scene8Closing 
            insights={insights}
            features={data.geoFeatures}
            isActive={activeScene === 7}
            onExploreClick={onNavigateToExplorer}
          />
        </div>
      </main>
      
      {/* Footer */}
      <Footer minimal />
    </div>
  );
}

export default StoryPage;
